import { CopyIcon, LockIcon, PenIcon } from "@xola/icons";
import { Badge, flash, Spinner, Tooltip } from "@xola/ui-kit";
import { useUnmount } from "ahooks";
import clsx from "clsx";
import React, { useRef, useState } from "react";
import { useMutation } from "react-query";
import { ApiKeyUser, Permission, UserRole, UserRolesCopy } from "types";
import { RevokeApiKeyButton } from "./RevokeApiKeyButton";
import { api } from "@/api/api";
import { IsGranted } from "@/components/Common/IsGranted";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { getSeller } from "@/utils/getSeller";

interface ApiKeyItemProps {
    apiKey: ApiKeyUser;
    onEdit: (apiKey: ApiKeyUser) => void;
}

export const ApiKeyItem = ({ apiKey, onEdit }: ApiKeyItemProps) => {
    const [showCopied, setShowCopied] = useState(false);
    const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useUnmount(() => {
        if (copiedTimeoutRef.current) {
            clearTimeout(copiedTimeoutRef.current);
        }
    });

    const { mutateAsync: fetchApiKey, isLoading } = useMutation(async () => {
        const response = await api.get<ApiKeyUser>(`/sellers/${getSeller().id}/apiKeys/${apiKey.id}`);
        return response.data.apiKey;
    });

    const handleCopyClick = async () => {
        if (isLoading) {
            return;
        }

        let actualKey: string | undefined;
        try {
            actualKey = await fetchApiKey();
        } catch {
            flash.danger("Unable to copy API key right now. Please try again.");
            return;
        }

        if (!actualKey) {
            flash.danger("Unable to copy API key right now. Please try again.");
            return;
        }

        navigator.clipboard.writeText(actualKey);
        setShowCopied(true);
        if (copiedTimeoutRef.current) {
            clearTimeout(copiedTimeoutRef.current);
        }
        copiedTimeoutRef.current = setTimeout(() => setShowCopied(false), 2000);
    };

    return (
        <div
            className={clsx("flex w-full flex-col justify-between space-y-2 py-7 hover:bg-gray-hover")}
            data-testid="api-key-item"
        >
            <div className="flex w-full flex-row justify-between">
                <div className="flex flex-row items-center space-x-4">
                    <div className="text-lg font-bold" data-testid="api-key-item-name">
                        <NoTranslate>{apiKey.name}</NoTranslate>
                    </div>
                </div>

                <div className="flex items-center space-x-2">
                    <IsGranted permissions={Permission.MODIFY_ACCESS_MANAGER_REVOKE_ACCESS}>
                        <RevokeApiKeyButton apiKey={apiKey} />
                    </IsGranted>

                    <SecondaryButton size="small" data-testid="api-key-item-edit-button" onClick={() => onEdit(apiKey)}>
                        <PenIcon />
                    </SecondaryButton>
                </div>
            </div>

            <div className="flex items-center space-x-1" data-testid="api-key-item-masked-key">
                <Tooltip placement="top" content={<span>{showCopied ? "Copied!" : "Click to copy"}</span>}>
                    <span
                        className="flex cursor-pointer items-center space-x-1 hover:underline"
                        data-testid="api-key-item-copy"
                        onClick={handleCopyClick}
                    >
                        <LockIcon />
                        <span className="font-mono text-sm text-gray-darker">{apiKey.apiKey}</span>
                        {isLoading ? <Spinner size="tiny" /> : <CopyIcon className="h-4 w-4 text-gray-darker" />}
                    </span>
                </Tooltip>
            </div>

            <div className="flex w-full flex-row justify-between">
                <div className="w-full space-y-1">
                    <RoleBadges roles={apiKey.roles} />
                </div>
            </div>
        </div>
    );
};

const RoleBadges = ({ roles }: { roles: string[] }) => {
    const sortedRoles = roles.filter((role) => role !== UserRole.API_KEY).sort((a, b) => a.localeCompare(b));

    return (
        <div className="flex flex-wrap gap-2" data-testid="api-key-item-roles">
            {sortedRoles.map((role) => (
                <Badge key={role} color="secondary" size="small">
                    {UserRolesCopy[role as keyof typeof UserRolesCopy] ?? role}
                </Badge>
            ))}
        </div>
    );
};
